/**
 * FastTracker II XM loader.
 *
 * XM is far more forgiving to parse than MOD but far less forgiving to parse
 * *wrongly*: chunk sizes are self-describing, and files in the wild routinely
 * disagree with the spec about them. Every seek here is driven by the file's own
 * size fields, with bounds checks, so a bad size truncates one instrument rather
 * than derailing the whole load.
 */
import {
  CELL_EFFECT,
  CELL_INSTRUMENT,
  CELL_NOTE,
  CELL_PARAM,
  CELL_STRIDE,
  CELL_VOLUME,
  createInstrument,
  createPattern,
  createSample,
  LOOP_FORWARD,
  LOOP_NONE,
  LOOP_PINGPONG,
  type Envelope,
  type Instrument,
  type LoadResult,
  type LoadWarning,
  type LoopType,
  type Sample,
  type Song,
} from '../types';
import { ByteReader } from './binary';

const XM_SIGNATURE = 'Extended Module: ';

export function isXmFile(buffer: ArrayBuffer): boolean {
  const r = new ByteReader(buffer);
  if (r.length < 80) return false;
  return r.peekStr(0, 17).trimEnd() === XM_SIGNATURE.trimEnd();
}

export function loadXm(buffer: ArrayBuffer): LoadResult {
  const r = new ByteReader(buffer);
  const warnings: LoadWarning[] = [];

  if (r.length < 80 || r.peekStr(0, 17).trimEnd() !== XM_SIGNATURE.trimEnd()) {
    throw new Error('Not an XM file (missing "Extended Module: " signature)');
  }

  r.seek(17);
  const name = r.str(20);
  r.skip(1); // 0x1A separator
  const trackerName = r.str(20);
  const version = r.u16le();

  const headerStart = 60;
  const headerSize = r.u32le();
  const songLength = r.u16le();
  const restartPosition = r.u16le();
  let channels = r.u16le();
  const patternCount = r.u16le();
  const instrumentCount = r.u16le();
  const flags = r.u16le();
  let initialSpeed = r.u16le();
  let initialTempo = r.u16le();

  if (channels < 1 || channels > 64) {
    warnings.push({ code: 'channels', message: `Channel count ${channels} out of range; clamped.` });
    channels = Math.max(1, Math.min(64, channels || 4));
  }
  if (initialSpeed < 1 || initialSpeed > 31) initialSpeed = 6;
  if (initialTempo < 32 || initialTempo > 255) initialTempo = 125;

  const order = new Uint8Array(256);
  for (let i = 0; i < 256; i++) order[i] = r.u8();

  // --- Patterns -----------------------------------------------------------
  // The header size field is authoritative for where pattern data begins;
  // trusting our own field-by-field cursor breaks on files with padded headers.
  let pos = headerStart + headerSize;
  const patterns = [];
  for (let p = 0; p < patternCount; p++) {
    if (pos >= r.length) {
      warnings.push({
        code: 'pat-eof',
        message: `File ended after ${p} of ${patternCount} patterns; remainder filled with empty patterns.`,
      });
      break;
    }
    r.seek(pos);
    const patHeaderSize = r.u32le();
    const packingType = r.u8();
    let rows = r.u16le();
    const packedSize = r.u16le();

    if (rows < 1 || rows > 256) {
      warnings.push({ code: 'rows', message: `Pattern ${p} claims ${rows} rows; using 64.` });
      rows = 64;
    }
    if (packingType !== 0) {
      warnings.push({
        code: 'packing',
        message: `Pattern ${p} uses unknown packing type ${packingType}; read as unpacked.`,
      });
    }

    const pattern = createPattern(rows, channels);
    const dataStart = pos + (patHeaderSize > 0 ? patHeaderSize : 9);
    if (packedSize > 0) {
      r.seek(dataStart);
      unpackPattern(r, pattern.data, rows, channels, dataStart + packedSize);
    }
    patterns.push(pattern);
    pos = dataStart + packedSize;
  }
  while (patterns.length < patternCount) patterns.push(createPattern(64, channels));

  // --- Instruments --------------------------------------------------------
  const instruments: Instrument[] = [];
  for (let i = 0; i < instrumentCount; i++) {
    if (pos >= r.length) {
      warnings.push({
        code: 'inst-eof',
        message: `File ended after ${i} of ${instrumentCount} instruments.`,
      });
      break;
    }
    const result = readInstrument(r, pos, version, warnings, i);
    instruments.push(result.instrument);
    pos = result.next;
  }
  while (instruments.length < instrumentCount) instruments.push(createInstrument());

  const defaultPanning = new Uint8Array(channels).fill(128);

  const song: Song = {
    name,
    trackerName: trackerName || `XM v${(version >> 8).toString()}.${(version & 0xff).toString().padStart(2, '0')}`,
    format: 'xm',
    mode: 'ft2',
    channels,
    order,
    songLength: Math.max(1, Math.min(256, songLength)),
    restartPosition: restartPosition < songLength ? restartPosition : 0,
    patterns,
    instruments,
    initialSpeed,
    initialTempo,
    linearFrequency: (flags & 1) !== 0,
    amigaLimits: false,
    defaultPanning,
  };

  return { song, warnings };
}

/**
 * XM's pattern packing: a leading byte with bit 7 set is a field mask; anything
 * else is a bare note followed by the other four fields.
 */
function unpackPattern(
  r: ByteReader,
  data: Uint8Array,
  rows: number,
  channels: number,
  limit: number,
): void {
  for (let row = 0; row < rows; row++) {
    for (let ch = 0; ch < channels; ch++) {
      if (r.pos >= limit) return;
      const dst = (row * channels + ch) * CELL_STRIDE;
      const first = r.u8();
      if (first & 0x80) {
        if (first & 0x01) data[dst + CELL_NOTE] = r.u8();
        if (first & 0x02) data[dst + CELL_INSTRUMENT] = r.u8();
        if (first & 0x04) data[dst + CELL_VOLUME] = r.u8();
        if (first & 0x08) data[dst + CELL_EFFECT] = r.u8();
        if (first & 0x10) data[dst + CELL_PARAM] = r.u8();
      } else {
        data[dst + CELL_NOTE] = first;
        data[dst + CELL_INSTRUMENT] = r.u8();
        data[dst + CELL_VOLUME] = r.u8();
        data[dst + CELL_EFFECT] = r.u8();
        data[dst + CELL_PARAM] = r.u8();
      }
    }
  }
}

function readEnvelope(points: number[][], count: number, type: number, sustain: number, loopStart: number, loopEnd: number): Envelope {
  const n = Math.max(0, Math.min(12, count));
  const pts = points.slice(0, n).map(([x, y]) => ({ x, y }));
  if (pts.length === 0) pts.push({ x: 0, y: 32 }, { x: 24, y: 32 });
  return {
    points: pts,
    enabled: (type & 1) !== 0,
    sustainEnabled: (type & 2) !== 0,
    loopEnabled: (type & 4) !== 0,
    sustainPoint: Math.min(sustain, pts.length - 1),
    loopStart: Math.min(loopStart, pts.length - 1),
    loopEnd: Math.min(loopEnd, pts.length - 1),
  };
}

function readInstrument(
  r: ByteReader,
  offset: number,
  _version: number,
  warnings: LoadWarning[],
  index: number,
): { instrument: Instrument; next: number } {
  r.seek(offset);
  let instSize = r.u32le();
  const name = r.str(22);
  r.skip(1); // instrument type, always 0 and always ignored
  const sampleCount = r.u16le();

  const inst = createInstrument(name);

  // Guard against sizes that would move the cursor backwards or off the end.
  if (instSize < 29 || offset + instSize > r.length + 4) {
    instSize = sampleCount > 0 ? 263 : 29;
  }

  if (sampleCount === 0) {
    return { instrument: inst, next: offset + instSize };
  }

  const sampleHeaderSize = r.u32le() || 40;
  for (let i = 0; i < 96; i++) inst.sampleMap[i] = r.u8();

  const volPoints: number[][] = [];
  for (let i = 0; i < 12; i++) volPoints.push([r.u16le(), r.u16le()]);
  const panPoints: number[][] = [];
  for (let i = 0; i < 12; i++) panPoints.push([r.u16le(), r.u16le()]);

  const volCount = r.u8();
  const panCount = r.u8();
  const volSustain = r.u8();
  const volLoopStart = r.u8();
  const volLoopEnd = r.u8();
  const panSustain = r.u8();
  const panLoopStart = r.u8();
  const panLoopEnd = r.u8();
  const volType = r.u8();
  const panType = r.u8();
  inst.vibratoType = r.u8();
  inst.vibratoSweep = r.u8();
  inst.vibratoDepth = r.u8();
  inst.vibratoRate = r.u8();
  inst.volumeFadeout = r.u16le();

  inst.volumeEnvelope = readEnvelope(volPoints, volCount, volType, volSustain, volLoopStart, volLoopEnd);
  inst.panningEnvelope = readEnvelope(panPoints, panCount, panType, panSustain, panLoopStart, panLoopEnd);

  // Sample headers sit immediately after the instrument header block.
  let hpos = offset + instSize;
  const headers: { sample: Sample; byteLength: number; is16: boolean }[] = [];
  for (let s = 0; s < sampleCount; s++) {
    if (hpos + 18 > r.length) break;
    r.seek(hpos);
    const byteLength = r.u32le();
    const loopStartBytes = r.u32le();
    const loopLengthBytes = r.u32le();
    const volume = Math.min(64, r.u8());
    const finetune = r.i8();
    const type = r.u8();
    const panning = r.u8();
    const relativeNote = r.i8();
    r.u8(); // reserved / packing byte
    const sname = r.str(22);

    const is16 = (type & 0x10) !== 0;
    const shift = is16 ? 1 : 0;

    const smp = createSample(sname);
    smp.volume = volume;
    smp.finetune = finetune;
    smp.panning = panning;
    smp.relativeNote = relativeNote;
    smp.bits = is16 ? 16 : 8;
    smp.length = byteLength >> shift;
    smp.loopStart = loopStartBytes >> shift;
    smp.loopLength = loopLengthBytes >> shift;

    const loopBits = type & 0x03;
    smp.loopType = (loopBits === 1 ? LOOP_FORWARD : loopBits === 2 ? LOOP_PINGPONG : LOOP_NONE) as LoopType;
    if (smp.loopLength <= 0) smp.loopType = LOOP_NONE;

    headers.push({ sample: smp, byteLength, is16 });
    hpos += sampleHeaderSize;
  }

  // ...and all sample *data* follows all the headers, in the same order.
  let dpos = hpos;
  for (const h of headers) {
    const avail = Math.max(0, Math.min(h.byteLength, r.length - dpos));
    if (avail < h.byteLength) {
      warnings.push({
        code: 'smp-trunc',
        message: `Instrument ${index + 1} sample "${h.sample.name}" truncated (${avail}/${h.byteLength} bytes).`,
      });
    }
    r.seek(dpos);
    h.sample.data = decodeDelta(r, avail, h.is16);
    h.sample.length = h.sample.data.length;
    // Clamp the loop into whatever data actually survived.
    if (h.sample.loopStart > h.sample.length) h.sample.loopStart = 0;
    if (h.sample.loopStart + h.sample.loopLength > h.sample.length) {
      h.sample.loopLength = Math.max(0, h.sample.length - h.sample.loopStart);
    }
    if (h.sample.loopLength <= 0) h.sample.loopType = LOOP_NONE;
    dpos += h.byteLength;
  }

  inst.samples = headers.map((h) => h.sample);
  // Any note pointing at a sample we failed to read falls back to the first.
  for (let i = 0; i < 96; i++) {
    if (inst.sampleMap[i] >= inst.samples.length) inst.sampleMap[i] = 0;
  }

  return { instrument: inst, next: dpos };
}

/**
 * XM sample data is stored as deltas, which is what lets it zip so well. The
 * running accumulator wraps at the sample's bit width — that wrap is load-
 * bearing, since plenty of samples rely on it to reach full scale.
 */
function decodeDelta(r: ByteReader, byteCount: number, is16: boolean): Float32Array {
  if (is16) {
    const frames = byteCount >> 1;
    const out = new Float32Array(frames);
    let acc = 0;
    for (let i = 0; i < frames; i++) {
      const lo = r.u8();
      const hi = r.u8();
      let delta = (hi << 8) | lo;
      if (delta > 32767) delta -= 65536;
      acc = (acc + delta) & 0xffff;
      const signed = acc > 32767 ? acc - 65536 : acc;
      out[i] = signed / 32768;
    }
    return out;
  }
  const out = new Float32Array(byteCount);
  let acc = 0;
  for (let i = 0; i < byteCount; i++) {
    let delta = r.u8();
    if (delta > 127) delta -= 256;
    acc = (acc + delta) & 0xff;
    const signed = acc > 127 ? acc - 256 : acc;
    out[i] = signed / 128;
  }
  return out;
}
